import { useCallback, useEffect, useMemo, useState } from 'react';
import { QrCode, X, Camera, Cpu, KeyRound } from 'lucide-react';
import { DataMatrixScanner } from '@/lib/qr/DataMatrixScanner';
import { Button } from '@/components/ui/button';
import { IconButton } from '@/components/ui/icon-button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTranslation } from '@/hooks/useTranslation';
import { KeyenceScannerCard } from '@/features/traceability/components/KeyenceScannerCard';
import { CameraScannerCard } from '@/features/traceability/components/CameraScannerCard';
import { ManualIdInput } from '@/features/traceability/components/ManualIdInput';

type ScanPopUpProps = {
  open: boolean;
  onClose: () => void;
  onDetected: (id: string) => void;
};

export function ScanPopUp({ open, onClose, onDetected }: ScanPopUpProps) {
  const [detectedId, setDetectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>('camera');
  const scanner = useMemo(() => new DataMatrixScanner(), []);
  const { t } = useTranslation();

  const handleDialogChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose]
  );

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setDetectedId(null);
      setActiveTab('camera');
    }
  }, [open]);

  // Handle tool detected from any scanner
  const handleToolDetected = useCallback(
    (toolId: string) => {
      setDetectedId(toolId);
      // Call the parent callback after a short delay for visual feedback
      setTimeout(() => {
        onDetected(toolId);
        onClose();
      }, 500);
    },
    [onDetected, onClose]
  );

  // Handle manual ID submission
  const handleManualSubmit = useCallback(
    (toolId: string) => {
      onDetected(toolId);
      onClose();
    },
    [onDetected, onClose]
  );

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-4xl gap-0 overflow-hidden border-none bg-card p-0 shadow-2xl backdrop-blur-sm"
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-border bg-gradient-to-r from-primary/10 to-transparent px-6 py-5 dark:from-primary/20">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <QrCode className="h-5 w-5" />
            </span>
            <DialogHeader className="gap-1 text-left">
              <DialogTitle className="section-title">{t('toolView.scanDialog.title')}</DialogTitle>
              <DialogDescription className="max-w-md text-sm text-muted-foreground">
                {t('toolView.scanDialog.description')}
              </DialogDescription>
            </DialogHeader>
          </div>
          <DialogClose asChild>
            <IconButton
              type="button"
              variant="outline"
              size="icon"
              icon={X}
              label={t('common.close')}
            />
          </DialogClose>
        </div>

        {/* Content with Tabs */}
        <div className="px-6 py-5">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-6 grid w-full grid-cols-3">
              <TabsTrigger value="camera" className="gap-2">
                <Camera className="h-4 w-4" />
                {t('toolView.scanDialog.tabs.camera')}
              </TabsTrigger>
              <TabsTrigger value="keyence" className="gap-2">
                <Cpu className="h-4 w-4" />
                {t('toolView.scanDialog.tabs.keyence')}
              </TabsTrigger>
              <TabsTrigger value="manual" className="gap-2">
                <KeyRound className="h-4 w-4" />
                {t('toolView.scanDialog.tabs.manual')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="camera" className="mt-0">
              <div className="space-y-4">
                <CameraScannerCard
                  scanner={scanner}
                  detectedId={detectedId}
                  onDetected={handleToolDetected}
                  isActive={activeTab === 'camera'}
                />
              </div>
            </TabsContent>

            <TabsContent value="keyence" className="mt-0">
              <div className="space-y-4">
                <KeyenceScannerCard onDetected={handleToolDetected} />
              </div>
            </TabsContent>

            <TabsContent value="manual" className="mt-0">
              <div className="space-y-4">
                <ManualIdInput onSubmit={handleManualSubmit} />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Footer */}
        <div className="border-t border-border bg-card px-6 py-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">{t('toolView.scanDialog.helper')}</p>
            <DialogClose asChild>
              <Button variant="outline">{t('toolView.scanDialog.actions.cancel')}</Button>
            </DialogClose>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
